import React, { useEffect, useState, useRef } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { uploadVideo, getVideos, toggleVideo } from "../../services/AppService";
import closeButton from "../../assets/images/close.png";
import { BiTrash } from "react-icons/bi";
import { resolveMediaUrl } from "../../utility/media";
import ConfirmActionModal from "../../components/AdminComponents/Modals/ConfirmActionModal";
import MessageModal from "../../components/AdminComponents/Modals/MessageModal";
import UploadLoader from "../../components/UploadLoader";

const VideoForm = () => {

    const navigate = useNavigate();
    const { workOrderId } = useParams();

    const [file, setFile] = useState(null);
    const [videos, setVideos] = useState([]);
    const [isUploading, setIsUploading] = useState(false);
    const [uploadProgress, setUploadProgress] = useState(null);

    const fileInputRef = useRef(null);

    const [confirmModal, setConfirmModal] = useState({
        open: false,
        action: null,
        payload: null
    });

    const [messageModal, setMessageModal] = useState({
        open: false,
        type: "success",
        title: "",
        message: ""
    });

    const fetchVideos = async () => {
        const res = await getVideos(workOrderId);
        setVideos(res || []);
    };

    useEffect(() => {
        if (workOrderId) fetchVideos();
    }, [workOrderId]);

    // ✅ Upload click
    const handleUpload = () => {
        if (!file) {
            setMessageModal({
                open: true,
                type: "error",
                title: "Error",
                message: "Please select a video file"
            });
            return;
        }

        setConfirmModal({
            open: true,
            action: "upload",
            payload: null
        });
    };

    // ✅ Confirm action (upload/delete)
    const handleConfirm = async () => {
        const { action, payload } = confirmModal;
        setConfirmModal({ open: false, action: null, payload: null });

        try {
            if (action === "upload") {
                setIsUploading(true);
                setUploadProgress(0);
                await uploadVideo(workOrderId, file, (progress) => {
                    setUploadProgress(progress);
                });

                setFile(null);
                if (fileInputRef.current) {
                    fileInputRef.current.value = "";
                }

                setMessageModal({
                    open: true,
                    type: "success",
                    title: "Success",
                    message: "Video uploaded successfully"
                });
            }

            if (action === "delete") {
                await toggleVideo(payload);

                setMessageModal({
                    open: true,
                    type: "success",
                    title: "Success",
                    message: "Video deleted successfully"
                });
            }

            fetchVideos();

        } catch (error) {
            const message =
                error?.response?.data?.message ||
                error?.message ||
                "Something went wrong";

            setMessageModal({
                open: true,
                type: "error",
                title: "Error",
                message
            });
        } finally {
            setIsUploading(false);
            setUploadProgress(null);
        }
    };

    // ✅ Delete click
    const handleDelete = (id) => {
        setConfirmModal({
            open: true,
            action: "delete",
            payload: id
        });
    };

    return (
        <div className="bg-white p-4 rounded shadow-md">

            {/* Header */}
            <div className="flex justify-between items-center border-b pb-3 mb-4">
                <h4 className="text-lg md:text-xl font-semibold text-blue uppercase">
                    Add / Edit Video
                </h4>
                <img
                    src={closeButton}
                    alt="Close"
                    className="w-6 h-6 cursor-pointer"
                    onClick={() => navigate(-1)}
                />
            </div>

            {/* Upload */}
            <div className="flex flex-col sm:flex-row gap-3 items-center mb-4">
                <input
                    type="file"
                    accept="video/*,video/mp4,video/mkv,video/webm"
                    ref={fileInputRef}
                    onChange={(e) => setFile(e.target.files[0])}
                    className="border p-2 rounded w-full sm:w-auto bg-white"
                />

                <button
                    onClick={handleUpload}
                    disabled={!file}
                    className="staff-btn-save disabled:opacity-50"
                >
                    Add
                </button>
            </div>

            {/* Table */}
            <div className="overflow-x-auto">
                <table className="w-full border">
                    <thead className="bg-[#5f6db3] text-white">
                        <tr>
                            <th className="p-2 border">Sr</th>
                            <th className="p-2 border">Video</th>
                            <th className="p-2 border">Delete</th>
                        </tr>
                    </thead>

                    <tbody>
                        {/* ✅ EMPTY STATE */}
                        {videos.length === 0 ? (
                            <tr>
                                <td colSpan="3" className="p-4 text-center text-gray-500">
                                    No videos uploaded
                                </td>
                            </tr>
                        ) : (
                            videos.map((video, idx) => (
                                <tr key={video.id} className="text-center">

                                    <td className="border p-2">{idx + 1}</td>

                                    <td className="border p-2">
                                        <div className="w-full max-w-3xl mx-auto">
                                            <video
                                                controls
                                                className="w-full h-[250px] rounded-md"
                                            >
                                                <source
                                                    src={resolveMediaUrl(video.fileLocation)}
                                                    type="video/mp4"
                                                />
                                                Your browser does not support video
                                            </video>
                                        </div>
                                    </td>

                                    <td className="border p-2">
                                        <button
                                            onClick={() => handleDelete(video.id)}
                                            className="text-red-600 text-xl"
                                        >
                                            <BiTrash />
                                        </button>
                                    </td>

                                </tr>
                            ))
                        )}
                    </tbody>
                </table>
            </div>

            {/* Confirm Modal */}
            <ConfirmActionModal
                open={confirmModal.open}
                onClose={() => setConfirmModal({ open: false })}
                onConfirm={handleConfirm}
                title={
                    confirmModal.action === "upload"
                        ? "Confirm Upload"
                        : "Confirm Delete"
                }
                message={
                    confirmModal.action === "upload"
                        ? "Are you sure you want to upload this video?"
                        : "Are you sure you want to delete this video?"
                }
            />

            {/* Message Modal */}
            <MessageModal
                open={messageModal.open}
                onClose={() => setMessageModal({ ...messageModal, open: false })}
                {...messageModal}
            />

            {isUploading && (
                <UploadLoader
                    text="Uploading drone video, please wait..."
                    progress={uploadProgress}
                />
            )}

        </div>
    );
};

export default VideoForm;